// Device identity.
//  • Web/desktop  → getDeviceName(): a stable opaque per-browser id (localStorage).
//  • Mobile APK   → getMobileDeviceId(): the hardware device id from Capacitor's
//                   Device plugin, used to hard-bind an account to one phone.
const KEY = 'se_deviceName';
const MKEY = 'se_mobileDeviceId';
const TKEY = 'se_deviceToken';

// ── Server-issued device token ──────────────────────────────────────────────
// Returned once by VerifyUserDevice/BindUserDevice when this device gets bound;
// it (not the raw device id) is what proves "same device" from then on. Kept in
// localStorage, mirrored in a 1-year cookie on the web so clearing one store does
// not immediately force an OTP. Keyed by MOBILE NUMBER, so two accounts sharing
// one PC each keep their own token; kept across logout so re-login on the same
// device needs no OTP.
const tkey = (mobile) => `${TKEY}:${String(mobile || '').trim()}`;
export function getDeviceToken(mobile) {
  let t = null;
  try { t = localStorage.getItem(tkey(mobile)); } catch { /* ignore */ }
  if (!t && typeof document !== 'undefined') {
    const m = document.cookie.match(new RegExp(`(?:^|; )se_dt_${String(mobile || '').trim()}=([0-9a-f]{32})`));
    if (m) { t = m[1]; try { localStorage.setItem(tkey(mobile), t); } catch { /* ignore */ } }
  }
  return t || '';
}
export function setDeviceToken(mobile, t) {
  try { if (t) localStorage.setItem(tkey(mobile), t); else localStorage.removeItem(tkey(mobile)); } catch { /* ignore */ }
  if (typeof document !== 'undefined' && !isNativeApp()) {
    const name = `se_dt_${String(mobile || '').trim()}`;
    document.cookie = t ? `${name}=${t}; Max-Age=31536000; Path=/; SameSite=Lax` : `${name}=; Max-Age=0; Path=/`;
  }
}
export const clearDeviceToken = (mobile) => setDeviceToken(mobile, '');

export function getDeviceName() {
  let d = localStorage.getItem(KEY);
  if (!d) {
    d = 'web-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(KEY, d);
  }
  return d;
}

// True only inside the Capacitor native shell (the installed APK), not the browser.
export function isNativeApp() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;
}

// Human-readable device description stored in Evt_Users.DeviceModel so a Super
// Admin can see WHICH device holds the binding before resetting it.
export async function getDeviceModel() {
  try {
    const Device = window.Capacitor?.Plugins?.Device;
    if (isNativeApp() && Device?.getInfo) {
      const i = await Device.getInfo();
      return [i.manufacturer, i.model, i.osVersion && `Android ${i.osVersion}`].filter(Boolean).join(' ').slice(0, 100);
    }
  } catch { /* fall through to UA */ }
  const ua = navigator.userAgent || '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return `${browser}${os ? ' on ' + os : ''}`;
}

// Hardware device id for the mobile APK. Read via the global Capacitor plugin
// registry (no bundler import, so the web build is unaffected). Falls back to a
// persisted random id when the plugin is missing (e.g. dev in a browser).
export async function getMobileDeviceId() {
  try {
    const Device = window.Capacitor?.Plugins?.Device;
    if (Device?.getId) {
      const info = await Device.getId();
      const id = info?.identifier || info?.uuid;
      if (id) return String(id);
    }
  } catch { /* plugin unavailable — fall through */ }
  let d = localStorage.getItem(MKEY);
  if (!d) {
    d = 'dev-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(MKEY, d);
  }
  return d;
}
